import type React from "react";
import { useState } from "react";
import styled from "styled-components";
import { richTextHelper } from "@helpers";
import type {
	IModuleVeritreeTreesInForestFields,
	IVeritreeTreesInForestTab,
} from "@types-contentful";

const Container = styled.section`
	padding: 0 10% 40px 10%;

	@media (min-width: 834px) {
		padding: 0 7% 60px 7%;
	}
`;

const Headline = styled.h2`
	text-align: center;
	margin-bottom: 32px;
`;

const TabBar = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 8px;
	margin-bottom: 32px;

	@media (min-width: 834px) {
		flex-direction: row;
	}
`;

const TabButton = styled.button<{ $active: boolean }>`
	padding: 10px 24px;
	border-radius: 24px;
	border: ${({ $active }) => ($active ? "none" : "2px solid #121252")};
	background-color: ${({ $active }) => ($active ? "#121252" : "transparent")};
	color: ${({ $active }) => ($active ? "#FFFFFF" : "#121252")};
	font-weight: 600;
	font-size: 14px;
	cursor: pointer;
	transition: background-color 0.2s ease;

	&:hover {
		background-color: ${({ $active }) =>
			$active ? "#121252" : "rgba(18, 18, 82, 0.1)"} !important;
		color: ${({ $active }) => ($active ? "#FFFFFF" : "#121252")} !important;
		border: ${({ $active }) =>
			$active ? "none" : "2px solid #121252"} !important;
	}
`;

const TabContent = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 24px;

	@media (min-width: 834px) {
		flex-direction: row;
		align-items: center;
		justify-content: center;
		gap: 40px;
	}
`;

const TabImage = styled.img`
	width: 100%;
	max-width: 400px;
	aspect-ratio: 1 / 1;
	border-radius: 12px;
	object-fit: cover;

	@media (min-width: 834px) {
		width: 50%;
		max-width: 500px;
	}
`;

const TabText = styled.div`
	width: 100%;

	@media (min-width: 834px) {
		max-width: 400px;
	}

	h3 {
		margin-bottom: 16px;
		color: #121252;
	}

	p {
		color: #121252;
		line-height: 1.6;
	}
`;

const TreesInForestModule: React.FC<IModuleVeritreeTreesInForestFields> = ({
	header,
	tabs,
}) => {
	const [activeTab, setActiveTab] = useState(0);

	if (!tabs || tabs.length === 0) return null;

	const activeTabData = tabs[activeTab];
	const imageUrl = activeTabData?.fields?.image?.fields?.file?.url;

	return (
		<Container>
			<Headline>{header}</Headline>
			<TabBar>
				{tabs.map((tab: IVeritreeTreesInForestTab, index: number) => (
					<TabButton
						key={tab.sys.id}
						$active={index === activeTab}
						onClick={() => setActiveTab(index)}
					>
						{tab.fields.tabLabel}
					</TabButton>
				))}
			</TabBar>
			<TabContent key={activeTabData.sys.id}>
				{imageUrl && (
					<TabImage
						src={imageUrl.startsWith("http") ? imageUrl : `https:${imageUrl}`}
						alt={activeTabData.fields.name}
					/>
				)}
				<TabText>
					<h3>{activeTabData.fields.name}</h3>
					{richTextHelper(activeTabData.fields.description)}
				</TabText>
			</TabContent>
		</Container>
	);
};

export default TreesInForestModule;
